import { type ProviderDriverKind, type ProviderInstanceId } from "@t3tools/contracts";
import type { VariantProps } from "class-variance-authority";
import { memo, useMemo, useState } from "react";

import type { ProviderInstanceEntry } from "../../providerInstances";
import { cn } from "~/lib/utils";
import { buttonVariants } from "../ui/button";
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from "../ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { ComposerControl, ComposerControlChevron } from "./ComposerControl";
import { ProviderInstanceIcon } from "./ProviderInstanceIcon";
import {
  commitComposerModelSelection,
  deriveComposerModelPickerState,
  formatComposerModelLabel,
} from "./composerModelPicker.logic";
import type { ModelEsque } from "./providerIconUtils";

export const ComposerModelPicker = memo(function ComposerModelPicker(props: {
  activeInstanceId: ProviderInstanceId;
  model: string;
  lockedProvider: ProviderDriverKind | null;
  lockedContinuationGroupKey?: string | null;
  instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  modelOptionsByInstance: ReadonlyMap<ProviderInstanceId, ReadonlyArray<ModelEsque>>;
  activeProviderIconClassName?: string;
  compact?: boolean;
  disabled?: boolean;
  open?: boolean;
  triggerVariant?: VariantProps<typeof buttonVariants>["variant"];
  triggerClassName?: string;
  triggerAriaLabel?: string;
  runtimeLockedDescription?: string;
  onOpenChange?: (open: boolean) => void;
  getModelDisabledReason?: (instanceId: ProviderInstanceId, model: string) => string | null;
  onInstanceModelChange: (instanceId: ProviderInstanceId, model: string) => void;
}) {
  const [uncontrolledIsMenuOpen, setUncontrolledIsMenuOpen] = useState(false);
  const isMenuOpen = props.open ?? uncontrolledIsMenuOpen;
  const pickerState = useMemo(
    () =>
      deriveComposerModelPickerState({
        activeInstanceId: props.activeInstanceId,
        instanceEntries: props.instanceEntries,
        lockedProvider: props.lockedProvider,
        lockedContinuationGroupKey: props.lockedContinuationGroupKey ?? null,
        model: props.model,
        modelOptionsByInstance: props.modelOptionsByInstance,
      }),
    [
      props.activeInstanceId,
      props.instanceEntries,
      props.lockedContinuationGroupKey,
      props.lockedProvider,
      props.model,
      props.modelOptionsByInstance,
    ],
  );
  const triggerLabel = pickerState.selectedModel
    ? formatComposerModelLabel(pickerState.selectedModel)
    : props.model.toLowerCase();
  const selectedValue = pickerState.selectedModel?.slug ?? props.model;
  const activeEntry = pickerState.activeEntry;
  const duplicateDriverCount = props.instanceEntries.filter(
    (entry) => activeEntry !== null && entry.driverKind === activeEntry.driverKind,
  ).length;
  const showInstanceBadge = Boolean(activeEntry?.accentColor) || duplicateDriverCount > 1;
  const triggerAriaLabel =
    props.triggerAriaLabel ??
    (props.runtimeLockedDescription
      ? `Model: ${triggerLabel}. ${props.runtimeLockedDescription}`
      : `Model: ${triggerLabel}`);

  const setIsMenuOpen = (open: boolean) => {
    props.onOpenChange?.(open);
    if (props.open === undefined) {
      setUncontrolledIsMenuOpen(open);
    }
  };

  return (
    <Menu
      open={isMenuOpen}
      onOpenChange={(open) => {
        if (props.disabled) {
          setIsMenuOpen(false);
          return;
        }
        setIsMenuOpen(open);
      }}
    >
      <MenuTrigger
        render={
          <ComposerControl
            aria-label={triggerAriaLabel}
            variant={props.triggerVariant ?? "ghost"}
            data-chat-provider-model-picker="true"
            className={cn(
              "min-w-0 justify-between whitespace-nowrap",
              props.compact ? "max-w-42 shrink-0" : "max-w-48 shrink sm:max-w-56",
              props.triggerClassName,
            )}
            disabled={props.disabled}
          />
        }
      >
        <Tooltip>
          <TooltipTrigger render={<span className="flex min-w-0 flex-1 items-center gap-1.5" />}>
            {activeEntry ? (
              <ProviderInstanceIcon
                driverKind={activeEntry.driverKind}
                displayName={activeEntry.displayName}
                accentColor={activeEntry.accentColor}
                showBadge={showInstanceBadge}
                className="size-3.5"
                iconClassName={cn("size-3.5", props.activeProviderIconClassName)}
                indicatorBackground="var(--input)"
                badgeClassName="right-[-0.125rem] bottom-[-0.125rem] h-3 min-w-3 px-0.5 text-[7px]"
              />
            ) : null}
            <span className="min-w-0 flex-1 overflow-hidden truncate">{triggerLabel}</span>
          </TooltipTrigger>
          <TooltipPopup side="top">
            <span className="block">{triggerLabel}</span>
            {props.runtimeLockedDescription ? (
              <span className="mt-0.5 block text-muted-foreground">
                {props.runtimeLockedDescription}
              </span>
            ) : null}
          </TooltipPopup>
        </Tooltip>
        <span aria-hidden="true" className="flex items-center">
          <ComposerControlChevron />
        </span>
      </MenuTrigger>
      <MenuPopup
        align="start"
        className="max-h-86.5 w-max max-w-[min(20rem,calc(100vw-2rem))] overflow-hidden"
        style={{ minWidth: "max(8rem, var(--anchor-width))" }}
      >
        {pickerState.options.length > 0 && activeEntry ? (
          <MenuRadioGroup value={selectedValue}>
            {pickerState.options.map((option) => {
              const label = formatComposerModelLabel(option);
              const disabledReason =
                props.getModelDisabledReason?.(activeEntry.instanceId, option.slug) ?? null;
              return (
                <MenuRadioItem
                  key={option.slug}
                  value={option.slug}
                  disabled={Boolean(disabledReason)}
                  label={label}
                  aria-label={disabledReason ? `${label}: ${disabledReason}` : label}
                  className={cn(
                    "w-full min-w-0 text-sm sm:text-sm",
                    disabledReason &&
                      "data-disabled:pointer-events-auto data-disabled:cursor-not-allowed",
                  )}
                  // Base UI normalizes pointer and keyboard item activation through click.
                  onClick={() => {
                    commitComposerModelSelection({
                      activeEntry,
                      options: pickerState.options,
                      modelSlug: option.slug,
                      disabledReason,
                      onInstanceModelChange: props.onInstanceModelChange,
                      onClose: () => setIsMenuOpen(false),
                    });
                  }}
                >
                  <Tooltip>
                    <TooltipTrigger render={<span className="block min-w-0 truncate" />}>
                      {label}
                    </TooltipTrigger>
                    <TooltipPopup
                      side="right"
                      align="center"
                      className="max-w-72 text-balance leading-snug"
                    >
                      {disabledReason ? (
                        <>
                          <span className="block font-medium text-foreground">{label}</span>
                          <span className="mt-0.5 block">{disabledReason}</span>
                        </>
                      ) : (
                        label
                      )}
                    </TooltipPopup>
                  </Tooltip>
                </MenuRadioItem>
              );
            })}
          </MenuRadioGroup>
        ) : (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">No models available</p>
        )}
      </MenuPopup>
    </Menu>
  );
});
